import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Building2, Plus, Trash2, MapPin, Phone, UserPlus, Mail, Pencil, Image as ImageIcon, X } from "@/components/icons";
import Portal from "../../components/Portal";

const empty = {
  name: "",
  address: "",
  phone: "",
  logo_url: null,
  add_secretary: false,
  secretary_name: "",
  secretary_email: "",
  secretary_password: "",
};

export default function Offices() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const logoRef = useRef();

  const load = () => api.get("/offices").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) {
      toast.error("La imagen es muy grande (máx 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, logo_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (o) => {
    setEditingId(o.id);
    setForm({
      name: o.name || "",
      address: o.address || "",
      phone: o.phone || "",
      logo_url: o.logo_url || null,
      add_secretary: false,
      secretary_name: "",
      secretary_email: "",
      secretary_password: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update existing office (name/address/phone/logo only — secretary edits separately)
        await api.put(`/offices/${editingId}`, {
          name: form.name,
          address: form.address,
          phone: form.phone,
          logo_url: form.logo_url,
        });
        toast.success("Oficina actualizada");
      } else {
        const payload = {
          name: form.name,
          address: form.address,
          phone: form.phone,
          logo_url: form.logo_url,
        };
        if (form.add_secretary) {
          if (!form.secretary_name || !form.secretary_email || !form.secretary_password) {
            toast.error("Completa los datos de la secretaria");
            return;
          }
          payload.secretary_name = form.secretary_name;
          payload.secretary_email = form.secretary_email;
          payload.secretary_password = form.secretary_password;
        }
        await api.post("/offices", payload);
        toast.success(
          form.add_secretary
            ? "Oficina y secretaria creadas"
            : "Oficina agregada"
        );
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar oficina? También se eliminará la secretaria asignada (si existe).")) return;
    await api.delete(`/offices/${id}`);
    load();
  };

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="offices-page">
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      <div className="relative flex items-center justify-end gap-4">
        <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
          <div className="text-[13px] font-semibold text-black tracking-tight">
            Consultorios
          </div>
          <div className="text-[11px] text-black/45 mt-0.5">
            {list.length} {list.length === 1 ? "oficina registrada" : "oficinas registradas"}
          </div>
        </div>
        <div className="flex items-center gap-0.5 bg-white border border-black/[0.04] rounded-lg p-1">
          <button
            type="button"
            onClick={openCreate}
            data-testid="new-office-button"
            className="h-9 pl-2 pr-3 rounded-md bg-black text-white hover:bg-black/85 text-[13px] font-semibold flex items-center gap-1.5 transition"
          >
            <Plus className="w-4 h-4" strokeWidth={2} weight="regular" /> Nueva oficina
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((o) => (
          <div key={o.id} data-testid={`office-card-${o.id}`} className="bg-white rounded-xl border border-black/[0.05] p-4 relative hover:border-black/[0.12] transition">
            <div className="flex items-center gap-3">
              {o.logo_url ? (
                <img
                  src={o.logo_url}
                  alt={o.name}
                  className="w-12 h-12 rounded-lg object-cover bg-white"
                  data-testid={`office-logo-${o.id}`}
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-[#F2F3F5] flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-black/60" strokeWidth={1.75} weight="regular" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-black text-[15px] truncate">{o.name}</div>
                <div className="text-[11px] text-black/45">Oficina</div>
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-[13px] text-black/75">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-black/40 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                <span className="truncate">{o.address || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-black/40 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                <span className="truncate">{o.phone || "—"}</span>
              </div>
              {o.secretary_email && (
                <div className="mt-2 pt-2 border-t border-black/[0.05]">
                  <div className="text-[9px] uppercase font-semibold text-black/45 tracking-widest">
                    Secretaria
                  </div>
                  <div className="text-[13px] font-semibold text-black mt-0.5">
                    {o.secretary_name}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-black/55 mt-0.5 truncate">
                    <Mail className="w-3 h-3 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                    <span className="truncate">{o.secretary_email}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute top-3 right-3 flex items-center gap-1">
              <button
                onClick={() => openEdit(o)}
                data-testid={`office-edit-${o.id}`}
                title="Editar"
                className="w-7 h-7 rounded-md hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
              </button>
              <button
                onClick={() => del(o.id)}
                data-testid={`office-delete-${o.id}`}
                title="Eliminar"
                className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-black/40 hover:text-red-500 transition"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-3 bg-white rounded-xl border border-black/[0.05] p-8 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 rounded-md bg-[#F2F3F5] flex items-center justify-center mb-2">
              <Building2 className="w-5 h-5 text-black/60" strokeWidth={1.75} weight="regular" />
            </div>
            <div className="text-[13px] text-black/60">No tienes oficinas registradas.</div>
            <button onClick={openCreate} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-black hover:underline">
              <Plus className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Crear la primera
            </button>
          </div>
        )}
      </div>
      </div>

      {open && (
        <Portal>
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 fade-up"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setOpen(false); setEditingId(null); setForm(empty); } }}
        >
          <form onSubmit={submit} className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl w-full max-w-lg" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-black/[0.05]">
              <div>
                <div className="text-[15px] font-semibold text-black">{editingId ? "Editar oficina" : "Nueva oficina"}</div>
                <div className="text-[11px] text-black/45 mt-0.5">Datos y logo del consultorio</div>
              </div>
              <button type="button" onClick={() => { setOpen(false); setEditingId(null); setForm(empty); }} className="w-8 h-8 rounded-lg hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition">
                <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-black/10 bg-[#F2F3F5] flex items-center justify-center overflow-hidden">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-full h-full object-cover" data-testid="office-logo-preview" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-black/30" strokeWidth={1.75} weight="regular" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">Logo</div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => logoRef.current?.click()} className="h-8 px-3 rounded-md bg-black text-white text-[11px] font-semibold hover:bg-black/85 transition" data-testid="office-logo-upload">
                      Subir logo
                    </button>
                    {form.logo_url && (
                      <button type="button" onClick={() => setForm({ ...form, logo_url: null })} className="h-8 px-3 rounded-md bg-white border border-black/10 text-black text-[11px] font-semibold hover:bg-black/[0.04] inline-flex items-center gap-1 transition" data-testid="office-logo-remove">
                        <X className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Quitar
                      </button>
                    )}
                  </div>
                  <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={onPickLogo} />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">Nombre *</label>
                <input required placeholder="Nombre del consultorio" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" data-testid="office-name-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">Dirección</label>
                <input placeholder="Calle, sector, ciudad" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full mt-1 h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">Teléfono</label>
                <input placeholder="809 555 1234" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" />
              </div>
              {!editingId && (
                <div className="pt-3 border-t border-black/[0.05]">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.add_secretary} onChange={(e) => setForm({ ...form, add_secretary: e.target.checked })} data-testid="add-secretary-toggle" className="w-4 h-4 accent-black" />
                    <span className="text-[13px] font-semibold text-black inline-flex items-center gap-1.5">
                      <UserPlus className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                      Crear secretaria para esta oficina
                    </span>
                  </label>
                  {form.add_secretary && (
                    <div className="space-y-2 mt-3 pl-6">
                      <input placeholder="Nombre de la secretaria" value={form.secretary_name} onChange={(e) => setForm({ ...form, secretary_name: e.target.value })} className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" data-testid="secretary-name-input" />
                      <input type="email" placeholder="Email" value={form.secretary_email} onChange={(e) => setForm({ ...form, secretary_email: e.target.value })} className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" data-testid="secretary-email-input" />
                      <input type="password" placeholder="Contraseña (mín. 6 caracteres)" minLength={6} value={form.secretary_password} onChange={(e) => setForm({ ...form, secretary_password: e.target.value })} className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition" data-testid="secretary-password-input" />
                      <p className="text-[11px] text-black/50">Esta secretaria solo verá y gestionará las citas de esta oficina.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-4 pt-3 border-t border-black/[0.05]">
              <button type="button" onClick={() => { setOpen(false); setEditingId(null); setForm(empty); }} className="h-10 px-4 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[13px] font-semibold text-black transition">
                Cancelar
              </button>
              <button type="submit" className="h-10 px-5 rounded-lg bg-black text-white hover:bg-black/85 text-[13px] font-semibold transition" data-testid="office-submit-button">
                {editingId ? "Actualizar" : "Guardar"}
              </button>
            </div>
          </form>
        </div>
        </Portal>
      )}
    </div>
  );
}
